import React from "react";
import { useMediaQuery } from "@mui/material";
import { truncateAddress, truncateText } from "@/utils/truncateText";
import { ConditionalTooltip } from "@/pages/discountManagement/components/ConditionalTooltip";
import { Refund, RefundStatus } from "@/types";
import dayjs from "dayjs";
import {
  getCancellationPolicyText,
  getNetworkDisplayName,
} from "@/utils/utils";

interface RefundCardProps {
  refund: Refund;
  onAction: (refund: Refund, status: RefundStatus) => void;
}

const RefundCard: React.FC<RefundCardProps> = ({ refund, onAction }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="w-full rounded-xl bg-white px-3 py-5 text-sm shadow-md">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Booking ID</span>
          <ConditionalTooltip
            text={refund?.bookingCode}
            maxLength={20}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-[#1E293B]">
              {truncateText(refund?.bookingCode)}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Booking date</span>
          <ConditionalTooltip
            text={refund?.bookingDate}
            maxLength={30}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-[#1E293B]">
              {truncateText(
                dayjs(refund?.bookingDate).format("YYYY-MM-DD HH:mm:ss"),
                30,
              )}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Contact person</span>
          <ConditionalTooltip
            text={refund?.orderContactName}
            maxLength={15}
            isMobile={isMobile}
          >
            <span className="cursor-pointer font-semibold text-[#1E293B]">
              {truncateText(refund?.orderContactName, 15)}
            </span>
          </ConditionalTooltip>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Amount of refund</span>
          <span className="font-semibold text-[#1E293B]">
            {!refund?.totalRefundable ? "0" : `${refund?.totalRefundable} USDT`}
          </span>
        </div>
        <hr className="bg-button-background" />

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Network</span>
          <span className="font-semibold text-[#1E293B]">
            {!refund?.refundNetwork
              ? "-"
              : getNetworkDisplayName(refund?.refundNetwork)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Address to refund</span>
          <span className="font-semibold text-[#1E293B]">
            {refund?.walletAddress ? (
              <ConditionalTooltip
                text={refund?.walletAddress.toString()}
                maxLength={10}
                isMobile={isMobile}
              >
                <span className="cursor-pointer">
                  {truncateAddress(refund?.walletAddress.toString())}
                </span>
              </ConditionalTooltip>
            ) : (
              "-"
            )}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Request Date</span>
          <span className="font-semibold text-[#1E293B]">
            {dayjs(refund?.requestDate).format("YYYY/MM/DD")}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[#334155]">Cancellation Policy</span>
          <span className="text-right font-semibold text-[#1E293B]">
            {getCancellationPolicyText(
              refund?.cancelRestrictionType,
              refund?.requestDate || "",
              refund?.checkInDate,
              refund?.cancelRestrictionDay,
              refund?.cancelRestrictionTime,
            )}
          </span>
        </div>
      </div>

      <div className="mt-6 flex gap-4">
        {(() => {
          switch (refund?.status.toUpperCase()) {
            case RefundStatus.PENDING:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.PENDING)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Review
                </button>
              );
            case RefundStatus.APPROVED:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.APPROVED)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Complete Refund
                </button>
              );
            case RefundStatus.REJECTED:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.REJECTED)}
                  className="flex-1 rounded-lg bg-[#F75C5C] px-4 py-2 text-sm font-medium text-white"
                >
                  Reject
                </button>
              );
            case RefundStatus.COMPLETED:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.COMPLETED)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Completed
                </button>
              );
            default:
              return (
                <button
                  onClick={() => onAction(refund, RefundStatus.PENDING)}
                  className="flex-1 rounded-lg bg-text px-4 py-2 text-sm font-medium text-white"
                >
                  Review
                </button>
              );
          }
        })()}
      </div>
    </div>
  );
};

export default RefundCard;
